import React, { useMemo, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import { ChevronDown, ChevronRight, Download } from 'lucide-react';
import toast from 'react-hot-toast';
import Loader from '../../../shared/components/ui/Loader';
import {
  EmptyState,
  Field,
  FilterBar,
  SegmentedControl,
  Td,
  Th
} from '../../../shared/components/ui/primitives';
import {
  downloadSiteWalletExpensesCsv,
  getSiteWalletExpensesPage,
  getSiteWalletSpendReport
} from '../../services/api/implementation/impl';
import { ListFooter, compactMoney, dayKey, money, shortDate } from './siteWalletUi';

/** Rows per page inside an opened report row. */
const BREAKDOWN_PAGE_SIZE = 20;

const GROUPS = [
  { key: 'project', label: 'By project' },
  { key: 'user', label: 'By site engineer' },
  { key: 'category', label: 'By category' },
  { key: 'month', label: 'By month' }
];

const dateInput =
  'rounded-control border border-hairline-strong bg-surface-muted px-2 py-1 text-sm text-gray-700 outline-none focus:border-brand-700';

/**
 * Approved spend, as a chart and a table. From wallet and own pocket are
 * stacked: together they are what the site cost, apart they say who paid.
 * An Accountant sees their projects only; the server scopes it.
 */
const GROUP_HEADING = {
  project: 'Project',
  user: 'Site engineer',
  category: 'Category',
  month: 'Month'
};

/** The report row's filter on GET /expenses; a month narrows the date range to it. */
function breakdownParams(groupBy, groupKey, range) {
  const params = { status: 'APPROVED', from: range.from, to: range.to };
  if (groupBy === 'project') params.project_id = groupKey;
  if (groupBy === 'user') params.user_id = groupKey;
  if (groupBy === 'category') params.category_id = groupKey;
  if (groupBy === 'month') {
    const [y, m] = String(groupKey).split('-').map(Number);
    const first = `${groupKey}-01`;
    const last = `${groupKey}-${String(new Date(Date.UTC(y, m, 0)).getUTCDate()).padStart(2, '0')}`;
    params.from = range.from && range.from > first ? range.from : first;
    params.to = range.to && range.to < last ? range.to : last;
  }
  return params;
}

/** The expenses behind one report row, shown when the row is opened. */
const Breakdown = ({ groupBy, groupKey, range }) => {
  // Mounted per opened row and keyed on the date range, so a new grouping or
  // range starts again from page 1.
  const [page, setPage] = useState(1);
  const params = { ...breakdownParams(groupBy, groupKey, range), page, limit: BREAKDOWN_PAGE_SIZE };
  const expenses = useQuery({
    queryKey: ['siteWalletReportBreakdown', params],
    queryFn: () => getSiteWalletExpensesPage(params),
    placeholderData: keepPreviousData
  });
  if (expenses.isLoading) return <p className="text-sm text-gray-500">Loading expenses…</p>;
  if (expenses.isError)
    return <p className="text-sm text-danger-fg">Could not load the expenses.</p>;
  const rows = expenses.data?.rows ?? [];
  if (!rows.length) return <p className="text-sm text-gray-500">No approved expenses.</p>;
  return (
    <div className="space-y-2">
      <table className="min-w-full text-sm">
        <thead className="text-gray-500">
          <tr>
            <th className="py-2 pr-3 text-left font-medium">Date</th>
            {groupBy !== 'user' && (
              <th className="py-2 pr-3 text-left font-medium">Site engineer</th>
            )}
            {groupBy !== 'project' && <th className="py-2 pr-3 text-left font-medium">Project</th>}
            {groupBy !== 'category' && (
              <th className="py-2 pr-3 text-left font-medium">Category</th>
            )}
            <th className="py-2 pr-3 text-left font-medium">Note</th>
            <th className="py-2 pr-3 text-left font-medium">Bill</th>
            <th className="py-2 pr-3 text-right font-medium">From wallet</th>
            <th className="py-2 pr-3 text-right font-medium">Own pocket</th>
            <th className="py-2 text-right font-medium">Total</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((e) => (
            <tr key={e.id} className="border-t border-gray-200">
              <td className="whitespace-nowrap py-2 pr-3 text-gray-700">{shortDate(e.spent_on)}</td>
              {groupBy !== 'user' && (
                <td className="py-2 pr-3 text-gray-700">{e.user?.name ?? '—'}</td>
              )}
              {groupBy !== 'project' && (
                <td className="py-2 pr-3 text-gray-700">{e.project_name ?? '—'}</td>
              )}
              {groupBy !== 'category' && (
                <td className="py-2 pr-3 text-gray-700">{e.category_name ?? '—'}</td>
              )}
              <td className="max-w-xs py-2 pr-3 text-gray-600">{e.note || '—'}</td>
              <td className="py-2 pr-3">
                {e.bill_urls?.length ? (
                  e.bill_urls.map((url, i) => (
                    <a
                      key={url}
                      href={url}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(ev) => ev.stopPropagation()}
                      className="mr-2 text-brand-700 hover:underline"
                    >
                      Bill {i + 1}
                    </a>
                  ))
                ) : (
                  <span className="text-gray-400">—</span>
                )}
              </td>
              <td className="py-2 pr-3 text-right tabular-nums text-gray-600">
                {money(e.wallet_amount)}
              </td>
              <td className="py-2 pr-3 text-right tabular-nums text-gray-600">
                {money(e.own_amount)}
              </td>
              <td className="py-2 text-right font-medium tabular-nums">{money(e.amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <ListFooter
        meta={expenses.data?.meta}
        page={page}
        onPageChange={setPage}
        isFetching={expenses.isFetching}
      />
    </div>
  );
};

const SpendReportPanel = () => {
  const [groupBy, setGroupBy] = useState('project');
  const [range, setRange] = useState({ from: '', to: '' });
  const [open, setOpen] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const params = useMemo(
    () => ({ group_by: groupBy, from: range.from, to: range.to }),
    [groupBy, range]
  );
  const report = useQuery({
    queryKey: ['siteWalletSpend', params],
    queryFn: () => getSiteWalletSpendReport(params)
  });
  const rows = report.data ?? [];

  // A month reads left to right; everything else is a ranking, biggest first,
  // which is the order the server returns.
  const ordered =
    groupBy === 'month'
      ? [...rows].sort((a, b) => String(a.label).localeCompare(String(b.label)))
      : rows;
  const charted = groupBy === 'month' ? ordered : ordered.slice(0, 12);
  const totals = rows.reduce(
    (t, r) => ({
      amount: t.amount + r.amount,
      wallet: t.wallet + r.wallet_amount,
      own: t.own + r.own_amount,
      count: t.count + r.count
    }),
    { amount: 0, wallet: 0, own: 0, count: 0 }
  );

  const download = async () => {
    setDownloading(true);
    try {
      const blob = await downloadSiteWalletExpensesCsv({ from: range.from, to: range.to });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `site-wallet-expenses-${dayKey()}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Could not download the CSV');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="space-y-4">
      <FilterBar
        end={
          <>
            <Field label="From" size="caption">
              <input
                type="date"
                value={range.from}
                onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))}
                className={dateInput}
              />
            </Field>
            <Field label="To" size="caption">
              <input
                type="date"
                value={range.to}
                onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))}
                className={dateInput}
              />
            </Field>
            {(range.from || range.to) && (
              <button
                type="button"
                onClick={() => setRange({ from: '', to: '' })}
                className="mb-1.5 text-sm font-semibold text-brand-700 hover:underline"
              >
                Clear
              </button>
            )}
            <button
              type="button"
              disabled={downloading}
              onClick={download}
              title="Every expense in the range, any status"
              className="flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3.5 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              <Download size={14} /> {downloading ? 'Downloading…' : 'Export CSV'}
            </button>
          </>
        }
      >
        <SegmentedControl
          ariaLabel="Group by"
          value={groupBy}
          onChange={setGroupBy}
          options={GROUPS.map((g) => ({ value: g.key, label: g.label }))}
        />
      </FilterBar>

      {report.isFetching && !rows.length ? (
        <Loader />
      ) : !rows.length ? (
        <EmptyState title="No approved spend in this range." />
      ) : (
        <>
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="mb-3 flex items-baseline justify-between">
              <h3 className="text-sm font-medium text-gray-800">
                {GROUPS.find((g) => g.key === groupBy)?.label}
              </h3>
              {rows.length > charted.length && (
                <span className="text-sm text-gray-500">
                  Top {charted.length} shown, all {rows.length} are in the table
                </span>
              )}
            </div>
            <div style={{ height: 320 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={charted} margin={{ top: 4, right: 8, left: 8, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f2" vertical={false} />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 11, fill: '#64748b' }}
                    interval={0}
                    angle={-20}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: '#64748b' }}
                    tickFormatter={compactMoney}
                    width={64}
                  />
                  <Tooltip
                    cursor={{ fill: 'rgba(15, 118, 110, 0.06)' }}
                    contentStyle={{ fontSize: 12, borderRadius: 8 }}
                    formatter={(v) => money(v)}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="wallet_amount" name="From wallet" stackId="spend" fill="#0f766e" />
                  <Bar
                    dataKey="own_amount"
                    name="Own pocket"
                    stackId="spend"
                    fill="#94a3b8"
                    radius={[3, 3, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <Th>{GROUP_HEADING[groupBy]}</Th>
                    <Th align="right">Expenses</Th>
                    <Th align="right">From wallet</Th>
                    <Th align="right">Own pocket</Th>
                    <Th align="right">Total</Th>
                  </tr>
                </thead>
                <tbody>
                  {ordered.map((r) => {
                    const rowKey = `${groupBy}-${r.key}`;
                    const expanded = open === rowKey;
                    return (
                      <React.Fragment key={rowKey}>
                        <tr
                          className="cursor-pointer border-t border-gray-100 hover:bg-gray-50"
                          aria-expanded={expanded}
                          onClick={() => setOpen(expanded ? null : rowKey)}
                        >
                          <Td className="font-medium text-gray-800">
                            <span className="inline-flex items-center gap-1.5">
                              {expanded ? (
                                <ChevronDown size={14} className="text-gray-500" />
                              ) : (
                                <ChevronRight size={14} className="text-gray-500" />
                              )}
                              {r.label ?? '—'}
                            </span>
                          </Td>
                          <Td align="right" numeric className="text-gray-500">
                            {r.count}
                          </Td>
                          <Td align="right" numeric className="text-gray-500">
                            {money(r.wallet_amount)}
                          </Td>
                          <Td align="right" numeric className="text-gray-500">
                            {money(r.own_amount)}
                          </Td>
                          <Td align="right" numeric className="font-medium">
                            {money(r.amount)}
                          </Td>
                        </tr>
                        {expanded && (
                          <tr>
                            <td colSpan={5} className="bg-gray-50 px-4 py-3">
                              <Breakdown
                                key={`${range.from}|${range.to}`}
                                groupBy={groupBy}
                                groupKey={r.key}
                                range={range}
                              />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
                <tfoot className="border-t-2 border-gray-200 bg-gray-50 font-medium">
                  <tr>
                    <Td>Total</Td>
                    <Td align="right" numeric>
                      {totals.count}
                    </Td>
                    <Td align="right" numeric>
                      {money(totals.wallet)}
                    </Td>
                    <Td align="right" numeric>
                      {money(totals.own)}
                    </Td>
                    <Td align="right" numeric>
                      {money(totals.amount)}
                    </Td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SpendReportPanel;
